import React, { useState } from 'react';
import { Paper, Button, Stack, Box, Typography, Chip, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import DicomDropzone from '../PseudoDicomAnonymizerPage/DicomDropzone';
import FileCard from '../shared/FileCard';
import DePseudoStatsPanel from '../PseudoAnonymizerPage/DePseudoStatsPanel';
import { dePseudoAnonymizeDicomPII } from '../../services/dicomService';

const DicomDePseudoPanel = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // --- State ---
  const [fileItems, setFileItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);

  // --- Handlers ---
  const handleFilesAdded = (files) => {
    const newItems = files.map(f => ({
      id: crypto.randomUUID(),
      name: f.name,
      file: f,
      status: 'ready',
      progress: 0,
      blob: null,
      downloadName: null,
      error: '',
      stats: null,
    }));
    setFileItems(prev => [...prev, ...newItems]);
    setGlobalStats(null); // Reset global stats when new files arrive
  };

  const handleRemove = (id) => {
    if (isProcessing) return;
    setFileItems(prev => prev.filter(item => item.id !== id));
  };

  const handleDownloadFile = (item) => {
    if (!item.blob) return;
    const url = window.URL.createObjectURL(item.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = item.downloadName || `restored_${item.name}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleStart = async () => {
    if (fileItems.length === 0 || isProcessing) return;

    setIsProcessing(true);
    let totalMapping = 0;
    let totalReplaced = 0;

    // Filter only files that are ready or previously failed
    const pendingItems = fileItems.filter(f => f.status === 'ready' || f.status === 'error');

    for (const item of pendingItems) {
      // 1. Set status to processing
      setFileItems(prev => prev.map(f => 
        f.id === item.id ? { ...f, status: 'uploading', progress: 30, error: '' } : f
      ));

      // 2. Call the service
      const res = await dePseudoAnonymizeDicomPII(item.file);

      if (res.success) {
        const { blob, filename, stats } = res.data;
        
        // Accumulate global stats
        totalMapping += (stats?.mapping_total || stats?.mappingTotal || 0);
        totalReplaced += (stats?.occurrences_replaced_total || stats?.replacedCount || 0);

        // 3. Update individual file success
        setFileItems(prev => prev.map(f => 
          f.id === item.id ? { 
            ...f, 
            status: 'success', 
            progress: 100, 
            blob, 
            downloadName: filename,
            stats: stats 
          } : f
        ));
      } else {
        // 3. Update individual file error
        setFileItems(prev => prev.map(f => 
          f.id === item.id ? { 
            ...f, 
            status: 'error', 
            progress: 100, 
            error: res.error || t('common.error') 
          } : f
        ));
      }
    }

    // Update the final summary
    setGlobalStats({ 
      mapping_total: totalMapping, 
      occurrences_replaced_total: totalReplaced 
    });
    
    setIsProcessing(false);
    enqueueSnackbar(t('DicomDePseudo.batchFinished', 'Dávkové spracovanie ukončené.'), { variant: 'success' });
  };

  const handleDownloadAll = () => {
    const successfulItems = fileItems.filter(f => f.status === 'success' && f.blob);
    successfulItems.forEach(item => handleDownloadFile(item));
  };

  return (
    <Box>
      {/* Upload Dropzone */}
      <DicomDropzone onFilesAdded={handleFilesAdded} disabled={isProcessing} />

      {/* List of Files with individual stats */}
      <Stack spacing={1.5} sx={{ mt: 3, mb: 3 }}>
        {fileItems.map((item) => (
          <Box key={item.id}>
            <FileCard 
              fileItem={item} 
              isUploading={isProcessing && item.status === 'uploading'} 
              onRemove={() => handleRemove(item.id)}
              onDownload={() => handleDownloadFile(item)}
              // t={t} is removed because FileCard now uses useTranslation() hook internally
            />
            
            {item.status === 'success' && item.stats && (
               <Box sx={{ mt: -1, mb: 1.5, ml: 2, mr: 2 }}>
                  <DePseudoStatsPanel stats={item.stats} />
               </Box>
            )}
          </Box>
        ))}
      </Stack>

      {/* Batch Actions and Global Summary */}
      {fileItems.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 3, 
            border: '1px solid', 
            borderColor: 'divider' 
          }}
        >
          {/* Global Summary Bar - Appears after processing is done */}
          {!isProcessing && globalStats && globalStats.mapping_total > 0 && (
            <Box 
              sx={{ 
                mb: 2.5, 
                p: 2, 
                bgcolor: 'success.lighter', 
                borderRadius: 2, 
                border: '1px solid', 
                borderColor: 'success.light' 
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AssessmentIcon color="success" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                  {t('DicomDePseudo.summaryTitle', 'Súhrn dávky')}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t('DicomDePseudo.totalRestored', 'Celkom obnovené')}:
                  </Typography>
                  <Chip 
                    label={globalStats.occurrences_replaced_total} 
                    size="small" 
                    color="success" 
                    sx={{ fontWeight: 600, borderRadius: 1.5 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  ({fileItems.filter(f => f.status === 'success').length} {t('DicomDePseudo.filesProcessed', 'súborov úspešne spracovaných')})
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={<PlayArrowIcon />}
              onClick={handleStart}
              disabled={isProcessing || !fileItems.some(f => f.status === 'ready' || f.status === 'error')}
              sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
            >
              {isProcessing ? t('common.processing', 'Spracovávam...') : t('DicomDePseudo.start', 'Spustiť obnovu dát')}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAll}
              disabled={isProcessing || !fileItems.some(f => f.status === 'success')}
              sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
            >
              {t('DicomDePseudo.downloadAll', 'Stiahnuť všetko')}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default DicomDePseudoPanel;